import * as React from 'react';
import { memo, useMemo } from 'react';
import cn from 'classnames';
import { useTheme } from '../../../../../theme';
import { useTranslation } from 'react-i18next';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Field } from 'react-final-form';
import { PassengerDisabilitiesState } from '../types';
import { TravellerDisability } from '@websky/graphql';
import { CdnIcon, SafeHtml } from '@shared/ui';
import { isExistI18nTranslate } from '../../../../../utils';

interface Props {
	passengerId: number;
	availableDisabilities: TravellerDisability[];
	passengerDisabilitiesState: PassengerDisabilitiesState;
	fieldNameBasePath: string;
	isError?: boolean;
}
const List: React.FC<Props> = memo(props => {
	const theme = useTheme('PassengerForm').Disabilities;
	const { t, i18n } = useTranslation('Passenger');

	const hasDisabilitiesBottomText = isExistI18nTranslate('DisabilitiesBottomText', {
		ns: 'Passenger',
		fallbackLng: i18n.language
	});

	const idsToDisable = useMemo(() => {
		const ids: string[] = [];

		Object.entries(props.passengerDisabilitiesState.values ?? {}).forEach(([key, isChecked]) => {
			if (isChecked) {
				const disabilityInfo = props.availableDisabilities.find(
					availableDisability => availableDisability.id === key
				);
				ids.push(...(disabilityInfo?.notCombinedWithAny ?? []));
			}
		});

		return ids;
	}, [props.passengerDisabilitiesState]);

	return (
		<div className={cn(theme.disabilities, { [theme.disabilities_error]: props.isError })}>
			<div className={theme.disabilities__text}>
				<CdnIcon className={theme.disabilities__infoIcon} icon="Info/Details" />

				<SafeHtml
					tag="div"
					html={t(
						'Passengers with disabilities and other persons with reduced mobility and persons accompanying them shall board the aircraft before boarding'
					)}
				/>
			</div>

			<div className={theme.checkboxes}>
				{props.availableDisabilities.map(disability => (
					<Field
						name={`${props.fieldNameBasePath}.values.${disability.id}`}
						initialValue={false}
						type={'checkbox'}
						key={disability.id}
						render={({ input }) => (
							<FormControlLabel
								label={disability.name}
								classes={{ label: theme.checkbox_label }}
								control={
									<Checkbox
										color="primary"
										classes={{
											colorPrimary: theme.checkbox,
											checked: theme.checkbox_checked,
											disabled: theme.checkbox_disabled,
											root: theme.checkbox_root
										}}
										checked={input.checked}
										onChange={input.onChange}
										disabled={idsToDisable.includes(disability.id)}
									/>
								}
							/>
						)}
					/>
				))}
			</div>

			{hasDisabilitiesBottomText && (
				<SafeHtml className={theme.disabilities__bottomText} html={t('DisabilitiesBottomText')} />
			)}
		</div>
	);
});

export default List;
